import React from 'react';
import Collapse from './Collapse';
import Slide from './Slide';
import { Notification, useFela } from '../../index';
import { ThemeProps } from '../../Theme';
import { NotificationItemProps } from './NotificationProps';
import { NotificationProps } from '../../Molecules/Notification/NotificationProps';

const innerStyle = (): object => ({
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
});

const spacerStyle = ({ theme }: { theme: ThemeProps }): object => ({
  boxSizing: 'border-box',
  height: theme.dimension.spacing.xxxs.rem,
  [theme.breakpoints.horizontal.m.mediaQuery]: {
    height: theme.dimension.spacing.xxs.rem,
  },
});

/**
 * The 'NotificationItem' is the container for rendering the actual Notification passed into it as 'notificationComponent'.
 *
 * It provides functionality, passed into the Notification component e.g. auto hiding.
 * It combines 'Slide' and 'Collapse' in order to create a pleasing enter and exit animation.
 */
const NotificationItem = ({
  id,
  kind,
  link,
  message,
  onClose,
  onExited,
  open,
  requestClose = false,
  persist,
  autoHideDuration,
  timeout,
  title,
}: NotificationItemProps) => {
  const autoHideTimeoutRef = React.useRef<any>(null);
  const collapseTimeoutRef = React.useRef<any>(null);
  const [collapseIn, setCollapseIn] = React.useState<boolean>(true);

  const createAutoHideTimeout = React.useCallback(
    (duration = autoHideDuration) => {
      if (!open || !requestClose || persist || !onClose) {
        return;
      }
      autoHideTimeoutRef.current = setTimeout(() => {
        onClose(id);
      }, duration);
    },
    [autoHideDuration, open, requestClose, persist, onClose, id],
  );

  const removeAutoHideTimeout = React.useCallback(() => {
    if (!autoHideTimeoutRef.current) return;
    clearTimeout(autoHideTimeoutRef.current);
  }, []);

  // Notification will be removed automatically after `autoHideDuration`.
  React.useEffect(() => {
    createAutoHideTimeout();
    return () => removeAutoHideTimeout();
  }, [createAutoHideTimeout, removeAutoHideTimeout]);

  const handleClose = () => {
    if (onClose) {
      onClose(id);
    }
  };

  /** Remove timeout on user interaction. */
  const handleMouseEnter = () => {
    removeAutoHideTimeout();
  };

  /** Resume timeout with adjusted druation `resumeAutoHideDuration` (autoHideDuration / 2). */
  const handleMouseLeave = () => {
    // The duration in which a notification is removed from the state after is has been interacted with e.g. hovering/clicking.
    const resumeAutoHideDuration: number = autoHideDuration / 2;
    createAutoHideTimeout(resumeAutoHideDuration);
  };

  /** Called immediately after `Collapse` exited. */
  const handleExited = () => {
    if (onExited) {
      onExited(id);
    }
  };

  /**
   * Called immediately after `TransitionComponent` exited.
   * Will close `Collapse` after 175ms
   */
  const handleSlideExited = () => {
    collapseTimeoutRef.current = setTimeout(() => {
      setCollapseIn(prevState => !prevState);
    }, 175);
  };

  const componentProps: NotificationProps = {
    dmId: `notification-${id}`,
    kind,
    link,
    message,
    onClose: handleClose,
    title,
  };

  const { css } = useFela();

  return (
    <div data-dmid={`notification-item-${id}`}>
      <Collapse
        in={collapseIn}
        unmountOnExit
        timeout={200}
        onExited={handleExited}>
        <Slide appear in={open} timeout={timeout} onExited={handleSlideExited}>
          <div
            data-dmid="notification-item-inner"
            className={css(innerStyle)}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}>
            <div
              data-dmid="notification-item-spacer"
              className={css(spacerStyle)}
            />
            <Notification {...componentProps} />
          </div>
        </Slide>
      </Collapse>
    </div>
  );
};

export default React.memo(NotificationItem);
